import useAuthStore from "../stores/useAuthStore";
import { formatCurrencyAmount } from "./tillHelpers";

/**
 * BUG-2020 — advance-order cart-edit floor check.
 *
 * A retrieved Advance/Layaway order that already has payment collected against it
 * must never be saved with a revised total below the amount already paid. Additions,
 * qty increases, and any change that keeps the total at or above the paid amount are
 * always allowed -- only the save/complete action is gated, not individual cart edits
 * (a cashier may legitimately remove a line and add a different one back before
 * completing; blocking the intermediate state would fight normal editing).
 */

// Cents-scale epsilon so a floating-point total of e.g. 499.9999999 isn't
// misclassified as "below" a paid amount of 500.
const FLOOR_EPSILON = 0.01;

// BUG-2467: matches dataIntegrity.js BLOCK_THRESHOLD (DI-01).
const LINE_TOTAL_GAP_THRESHOLD = 1.0;

// Order value as its lines add up: active lines (not canceled, weight not 0) plus
// charges and round-off -- the same expected total DI-01 (assertOrderTotals) uses.
const activeLinesTotal = (cart) => {
  const lineItemSum = (cart?.items || []).reduce(
    (sum, item) => (item?.isCanceled === "Y" || item?.weight === 0 ? sum : sum + (Number(item?.nettotal) || 0)),
    0
  );
  const charges =
    (Number(cart?.serviceCharge) || 0) +
    (Number(cart?.tip) || 0) +
    (Number(cart?.deiveryCharge) || 0) +
    (Number(cart?.packingCharge) || 0) +
    (Number(cart?.others) || 0) +
    (Number(cart?.tcs) || 0);
  return lineItemSum + charges + (Number(cart?.roundOff) || 0);
};

/**
 * True only when this cart is a RETRIEVED Advance/Layaway/Preorder order that
 * has a real payment already collected against it. Scope, widened from the
 * original GPR-/preorder-only gate: any retrieval flow (Layaway, Preorder,
 * Advance) across Retail and Restaurant, keyed off the retrieval signal every
 * retrieve builder already stamps (isRetrived -- useParkedBills.js,
 * useCartHistory.js -- or salesHistory -- useOrderHistory.js,
 * restaurantOrderCleanup.js, salesHistoryUtils.js), not a URL path. A fresh,
 * not-yet-retrieved cart (mid-sale, never saved/reloaded) is NOT gated here --
 * only an order that was saved, then pulled back up, where the amount from
 * that prior save must not be undercut.
 *
 * A cart with no advance payment is NOT gated by the floor check: "No Advance
 * Payment Collected" must stay fully editable per the ticket.
 *
 * Return/Exchange are explicitly excluded: a return or exchange settled
 * against a paid Advance/Layaway order is EXPECTED to bring the total below
 * what was paid (that's the refund), so it must never trip this guard.
 */
export const isAdvanceOrderWithPayment = (cart) => {
  const isAdvanceOrLayaway = cart?.docType === "Advance" || cart?.layAway === "Y";
  if (!isAdvanceOrLayaway) return false;
  const isRetrievedOrder = cart?.isRetrived === "Y" || cart?.salesHistory === "Y";
  if (!isRetrievedOrder) return false;
  if (cart?.isReturn === true || cart?.isReturn === "Y") return false;
  const paid = Number(cart?.advancePayment ?? cart?.paid ?? 0);
  return Number.isFinite(paid) && paid > 0;
};

/**
 * The floor itself: the total amount already collected against this order,
 * across every payment recorded so far. `advancePayment` is a one-time
 * snapshot taken at retrieval (useOrderHistory.js's getSalesHistoryData sums
 * finReceiptPlan as of that moment) and is never updated again; if the
 * cashier then takes an ADDITIONAL payment in the same session before
 * completing, paymentController.js folds the old advancePayment into the new
 * `cart.paid` (paid = totalAmountAdded + advancePayment), so `paid` becomes
 * the larger, true cumulative figure while `advancePayment` stays stale at
 * the old, smaller snapshot. Take the max of the two so neither a
 * retrieval-only cart (paid not yet populated) nor a
 * retrieved-then-paid-more-in-session cart (advancePayment stale) can
 * understate what's actually been collected.
 */
export const getAdvancePaidAmount = (cart) => Math.max(Number(cart?.advancePayment ?? 0), Number(cart?.paid ?? 0));

/**
 * Returns null when `total` is safe to save/process for this cart, or a
 * user-facing message string when it would breach the advance-payment floor.
 * Equal-to-paid is explicitly allowed (ticket: "processed without requiring any
 * additional payment").
 *
 * The rule: once an advance/layaway order has X paid against it, its revised
 * value may be X or ABOVE, never below.
 *
 * - At ORDER-PROCESSING time (processOrderApproved passes `atOrderProcessing:
 *   true`) the rule is enforced for EVERY flow, every client -- an order can
 *   never be completed below what's already been collected.
 * - The EDIT-time enforcement points (global setCart, add/qty/delete, discount
 *   wrappers) are additionally scoped to GPR's /preorder route. The 2026-09-01
 *   widening (4f4a891c) re-keyed them off any retrieved Advance/Layaway cart,
 *   but ManagePreorder's retrieve AND Chocolala's /pos Sales-History retrieve
 *   both route through getSalesHistoryData (stamps salesHistory:"Y"), so that
 *   signal can't tell the flows apart -- only the route can. Chocolala rebuilds
 *   a retrieved advance basket item-by-item on /pos and must not be blocked
 *   mid-edit (deadlock: total transiently < paid); it is still blocked from
 *   COMPLETING below the paid amount, via the atOrderProcessing path.
 * - BUG-2467 (reopened, 2026-10-08): the EDIT-time check is now off on /preorder
 *   too. It blocked the cashier from replacing an item (delete, then add another),
 *   because the total drops below paid in between. The rule is enforced only when
 *   the order is saved -- Create Advance or Complete (processOrder /
 *   processOrderApproved pass atOrderProcessing) -- the same on /preorder and /pos.
 *   The edit-time call sites (setCart, addProduct, discount wrappers) stay wired
 *   and simply get null.
 */
export const advanceOrderFloorViolation = (cart, total, { atOrderProcessing = false } = {}) => {
  if (!atOrderProcessing) return null;
  if (!isAdvanceOrderWithPayment(cart)) return null;
  const revisedTotal = Number(total ?? cart?.total ?? 0);
  // 2026-09-01 (global setCart wiring): a genuine full Cancel/Void zeroes total AND
  // totalQty together (Restaurant's clearCart1, useSuperMarketHandlers.js,
  // useParkedBills.js -- same signature already relied on by resolveIsAdvanceFlag
  // above) without touching paid/advancePayment. Now that this check runs on every
  // setCart call site-wide instead of a few explicitly-checked call sites, a cancel
  // must not be misread as "discounted the paid order down to nothing" and blocked --
  // there's nothing left to protect once the order itself is being cancelled.
  // Deliberately keyed on totalQty, not totalAmount alone: a real 100%-discount/comp
  // on an order that STILL has line items (totalQty>0, total===0) is exactly the
  // "silently give away an already-paid order" case this check exists to catch, and
  // must still be blocked.
  const revisedQty = Number(cart?.totalQty ?? 0);
  if (revisedTotal === 0 && revisedQty === 0) return null;
  const paid = getAdvancePaidAmount(cart);
  // BUG-2467: at completion, also measure the order by the lines it will actually
  // carry. cart.total can be stale relative to its lines (a rejected edit that still
  // zeroed a line's weight left total = paid while the remaining lines summed lower),
  // and only checking total let that order complete. Same line sum DI-01
  // (assertOrderTotals) uses; only a gap above its ₹1 block threshold counts, so
  // sub-rupee rounding drift never blocks a completion.
  let effectiveTotal = revisedTotal;
  if (atOrderProcessing && Array.isArray(cart?.items)) {
    const linesTotal = activeLinesTotal(cart);
    if (Number.isFinite(linesTotal) && revisedTotal - linesTotal > LINE_TOTAL_GAP_THRESHOLD) effectiveTotal = linesTotal;
  }
  if (!Number.isFinite(effectiveTotal) || effectiveTotal >= paid - FLOOR_EPSILON) return null;
  // The ₹ symbol and 2-decimal formatting here used to be hardcoded, showing the
  // wrong symbol/precision on any non-INR till. This is a plain utility function
  // called from a Zustand store action (useCartStore.js's setCart) as well as
  // component hooks, so there's no tillData prop to thread through cleanly --
  // read it from useAuthStore.getState(), the same source components' own
  // tillData prop is populated from.
  const tillData = useAuthStore.getState()?.tillData;
  return `This order already has ${formatCurrencyAmount(tillData, paid)} paid. The revised order value cannot be lower than the amount already paid.`;
};

/**
 * True when a cart is about to complete as an Advance order (docType/layAway)
 * on its FIRST-EVER save (never retrieved), has no Advance line item left in
 * it (productSegment "AD"), and is being paid in full rather than a genuine
 * partial deposit. This is the "picked Advance, then swapped the Advance item
 * out for regular items" gap: cart.layAway/docType is stamped once when the
 * cashier selects the Advance document type and is never re-validated against
 * the cart's actual contents before checkout, so a fully-paid, all-regular-item
 * cart can still complete and sync via posOrderProcessor1 flagged as Advance
 * (confirmed live: chocolala_live AJ2 ADV/91 — real product, paid in full,
 * layAway:"Y", no Advance line at all).
 *
 * Scoped to NOT-retrieved carts only: a retrieved Advance/Layaway order being
 * settled in full is a normal, legitimate completion (its own docType flip to
 * "Invoice" is handled elsewhere) and must never trip this check — only a
 * first-time save (which can only ever reach posOrderProcessor1, never
 * confirmPOSLayawayOrder1) is in scope.
 *
 * BUG-2685 (CPE ADV/140, Chocolala test): "retrieved" means an order the server
 * already holds -- one from Sales History / Manage Pre-Order (salesHistory "Y"
 * or type "Layaway") or a retrieved estimate (estimateId). A parked bill is
 * stamped isRetrived "Y" on retrieve (selectParkedBill, Cart History) but was
 * never saved, so it is a first-time save and gets this check like a fresh
 * cart. Before, isRetrived alone skipped it: park an Advance with only real
 * items, retrieve, pay in full, Create Advance -> saved as an advance with no
 * Advance line, which Sales History then shows as "Receipt" with no Retrieve
 * Sale, so it can never be completed.
 *
 * BUG-2274: also excludes the /preorder screen entirely. This check's whole
 * premise is "a real Advance order has a dedicated AD product line, so a
 * missing one means the cashier swapped it out" -- true for SuperMarket's
 * Invoice->Advance dropdown (which does add a real AD product), but false for
 * Preorder's Create Advance flow, which uses ordinary preorder products for
 * the deposit and has never added an AD line by design. Without this
 * exclusion, a genuine Preorder Advance order paid in full (paid >= total)
 * satisfies every other condition here and gets wrongly blocked with
 * "Advance Item Missing." Same isPreOrderPage convention as cart.js's
 * add-time stock-check gate and BUG-2275's retrieve-time gate
 * (useParkedBills.js) -- this is the same family of gap, just at
 * checkout-validation time instead of stock-check time.
 */
export const isAdvanceOrderMissingAdvanceItem = (cart) => {
  const isAdvanceOrLayaway = cart?.docType === "Advance" || cart?.layAway === "Y";
  if (!isAdvanceOrLayaway) return false;
  const isSavedOrder = cart?.salesHistory === "Y" || cart?.type === "Layaway";
  const isRetrievedEstimate = cart?.isRetrived === "Y" && !!cart?.estimateId;
  if (isSavedOrder || isRetrievedEstimate) return false;
  if (typeof window !== "undefined" && window.location?.pathname?.includes("/preorder")) return false;
  if (cart?.isReturn === true || cart?.isReturn === "Y") return false;
  // weight > 0 matters: this app's delete zeroes an item's weight instead of
  // removing it from the array (see BUG-2173's identical guard in
  // SuperMarketModal.js), so a plain productSegment check alone would still
  // see a deleted Advance item as "present" and never fire.
  const hasAdvanceItem = (cart?.items || []).some((item) => item?.productSegment === "AD" && item?.weight > 0);
  if (hasAdvanceItem) return false;
  const paid = Number(cart?.paid ?? 0);
  const total = Number(cart?.total ?? 0);
  return Number.isFinite(paid) && Number.isFinite(total) && paid >= total;
};

/**
 * BUG-2069/BUG-2105 — whether a processOrderApproved(param, ...) call is the
 * one genuinely finalizing an order, vs. Create Advance (param === "layaway"),
 * which never finalizes regardless of payment state. Stamped onto the order
 * record at save time (processOrderApproved, where `param` is in scope) and
 * later read back by syncOrders via orders[i].isCompleteOrderAction -- it
 * cannot be re-derived from `param` inside syncOrders itself, which has no
 * such parameter in scope (that gap caused a live ReferenceError, see
 * useOrderProcessing.js's BUG-2105 regression comment).
 */
export const resolveIsCompleteOrderAction = (param) => param !== "layaway";

/**
 * BUG-2105 — isFinalizingAdvanceToInvoice (datepromised/deliveryDate reset):
 * true only when a retrieved Advance/Layaway order is being finalized by the
 * cashier's own action (isCompleteOrderClicked), not merely because it happens
 * to be fully paid (a standing state, not an event tied to this specific call).
 */
export const resolveIsFinalizingAdvanceToInvoice = (isRetrievedAdvance, isCompleteOrderClicked) => isRetrievedAdvance && isCompleteOrderClicked;

/**
 * BUG-2069/BUG-2105 — the isAdvance field sent to confirmPOSLayawayOrder1. The
 * backend (POSOrderDaoImpl.bookOrderForLayAwayorder) only creates Shipment +
 * Sales Invoice once this arrives "N". Gated on totalQty > 0 so a fully
 * cancelled order that once collected an advance (total/totalQty zeroed
 * together, but not paid/advancePayment/statusType -- see clearCart1/
 * useSuperMarketHandlers.js/useParkedBills.js) never opens the shipment/invoice
 * gate for an order with nothing left to ship. Non-layaway orders (Credit Sale,
 * Exchange settlement, plain Retail/Restaurant checkout) never satisfy
 * statusType === "layAway" and always resolve to "N", unaffected by this logic.
 */
export const resolveIsAdvanceFlag = (statusType, isRetrievedAdvance, totalQty, isCompleteOrderClicked) =>
  statusType === "layAway" && !(isRetrievedAdvance && totalQty > 0 && isCompleteOrderClicked) ? "Y" : "N";

/**
 * BUG-2371 — "Order Quantity Not Reflecting on Bill" (GPR Gandipet, ADV/GDP/103).
 * Removing a previously-synced line from an advance-paid order keeps it in the
 * cart at weight 0 with its sOrderlineID (cart.js), and BUG-2105's follow-up
 * sends it at qty 0 so the backend zeroes that row. When the SAME product is then
 * re-added as a new line (e.g. Piece -> Kilogram, which can never merge into the
 * old line), confirmPOSLayawayOrder1 received two lines for one product; the
 * backend resolved both to the same s_orderline row and the zero line, applied
 * last, wiped the re-added one -- header correct, only line qty/amount 0.
 *
 * Pairs each such zero line one-to-one with a newly added (no sOrderlineID)
 * active line of the same product: the new line takes over the old
 * sOrderlineID (so the backend UPDATEs that row with the new UOM/qty/amounts)
 * and the zero line is not sent. Unpaired zero lines are left alone, so
 * BUG-2105's delete-then-add-a-different-product case is unchanged. Grouped
 * (Custom Mix) and cancelled lines are never paired, matching the payload
 * builder's existing exclusions. Read-only: returns lookups keyed by lineId
 * and never mutates `items` (orders[i].items is the persisted db.orders record).
 */
export const pairReaddedAdvanceLines = (items = []) => {
  const idForLine = new Map();
  const skipLineIds = new Set();
  const productIdOf = (item) => item?.productId || item?.product?.mProductId || null;
  const isPairable = (item) => Boolean(item?.lineId) && Boolean(productIdOf(item)) && item?.isCanceled !== "Y" && !(item?.children?.length > 0);
  const newLines = (items || []).filter((item) => isPairable(item) && item.weight > 0 && !item.sOrderlineID);
  (items || [])
    .filter((item) => isPairable(item) && item.weight === 0 && item.sOrderlineID)
    .forEach((zeroLine) => {
      const match = newLines.find((line) => productIdOf(line) === productIdOf(zeroLine) && !idForLine.has(line.lineId));
      if (match) {
        idForLine.set(match.lineId, zeroLine.sOrderlineID);
        skipLineIds.add(zeroLine.lineId);
      }
    });
  return { idForLine, skipLineIds };
};